<div class="asset-borrow-dialog">
  <!-- Header -->
  <div class="dialog-header">
    <div class="header-content">
      <h2>{{ data.prdNameEn }}</h2>
      <span class="prd-code">{{ data.prdCode }}</span>
    </div>
    <div class="header-actions">
      <!-- Print Button -->
      <button mat-icon-button
              class="print-button"
              [matMenuTriggerFor]="printMenu"
              matTooltip="{{ 'asset.printLabel' | translate }}"
              *ngIf="locEnablePrinting || locEnableBrotherPrinting">
        <mat-icon>print</mat-icon>
      </button>
      <!-- The two shipping entries differ only in their last words, which the
           menu's default width used to cut off, leaving them reading as the
           same line twice. The names are the original ones; the panel is
           widened instead so nothing is clipped. -->
      <mat-menu #printMenu="matMenu" panelClass="print-label-menu">
        <button mat-menu-item (click)="printDymo()" *ngIf="locEnablePrinting">
          <mat-icon>print</mat-icon>
          DYMO Asset Label (1" x 3 1/2")
        </button>
        <button mat-menu-item (click)="printShippingLabelQrOnly()" *ngIf="locEnablePrinting">
          <mat-icon>print</mat-icon>
          DYMO Shipping Label (2 1/8" x 4") - QR (Assets)
        </button>
        <button mat-menu-item (click)="printShippingLabelBarcodeOnly()" *ngIf="locEnablePrinting">
          <mat-icon>print</mat-icon>
          DYMO Shipping Label (2 1/8" x 4") - Barcode (Products)
        </button>
        <!-- The customer's own labels, after the built-in ones. -->
        <button mat-menu-item *ngFor="let label of customLabels"
                (click)="printDesigned(label)">
          <mat-icon>print</mat-icon>
          {{ label.name }}
        </button>
        <button mat-menu-item (click)="printBrother()" *ngIf="locEnableBrotherPrinting">
          <mat-icon>print</mat-icon>
          Brother (36mm)
        </button>
      </mat-menu>
      <button mat-icon-button class="close-button" (click)="onClose()">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </div>

  <!-- Loading -->
  <div *ngIf="isLoading" class="loading-container">
    <mat-spinner diameter="40"></mat-spinner>
  </div>

  <!-- Content -->
  <div *ngIf="!isLoading" class="dialog-content">
    <mat-tab-group [(selectedIndex)]="selectedTabIndex" animationDuration="0ms">
      <!-- Active Borrows Tab -->
      <mat-tab [label]="'asset.activeBorrows' | translate">
        <div class="tab-content">
          <div class="borrows-list" *ngIf="activeBorrows.length > 0">
            <div class="borrow-item" *ngFor="let borrow of activeBorrows">
              <div class="borrow-info">
                <div class="borrow-header">
                  <strong class="borrower-name">{{ borrow.borrowedByName }}</strong>
                  <span class="borrow-qty-badge">
                    {{ borrow.remainingQuantity }}<ng-container *ngIf="borrow.quantity > borrow.remainingQuantity"> / {{ borrow.quantity }}</ng-container>
                  </span>
                </div>
                <div class="borrow-details">
                  <span class="destination" *ngIf="borrow.destinationNote">
                    <mat-icon class="detail-icon">inventory_2</mat-icon>
                    {{ borrow.destinationNote }}
                  </span>
                  <span class="storage-path" *ngIf="borrow.storagePath">
                    <mat-icon class="detail-icon">place</mat-icon>
                    {{ borrow.storagePath }}
                  </span>
                  <span class="due-date" *ngIf="borrow.expectedReturnDate">
                    <mat-icon class="detail-icon">event</mat-icon>
                    {{ 'asset.dueDate' | translate }}: {{ borrow.expectedReturnDate | date:'mediumDate' }}
                  </span>
                  <span class="borrowed-date">
                    <mat-icon class="detail-icon">schedule</mat-icon>
                    {{ 'asset.borrowedOn' | translate }}: {{ borrow.created_at }}
                  </span>
                </div>
              </div>
              <div class="borrow-actions">
                <!-- Partial return: choose how many of the remaining units come back -->
                <mat-form-field appearance="outline" class="return-qty-field" *ngIf="borrow.remainingQuantity > 1">
                  <mat-label>{{ 'quantity' | translate }}</mat-label>
                  <input matInput type="number" min="1" [max]="borrow.remainingQuantity"
                         [(ngModel)]="returnQty[borrow.productMovementHistoryID]"
                         [ngModelOptions]="{standalone: true}">
                </mat-form-field>
                <!-- Optional damage photos, uploaded with the return -->
                <input type="file" accept="image/*" multiple hidden #damagePhotoInput
                       (change)="onDamagePhotoSelected(borrow, $event)">
                <button mat-icon-button type="button" class="photo-attach-btn"
                        matTooltip="{{ 'asset.attachDamagePhotos' | translate }}"
                        (click)="damagePhotoInput.click()">
                  <mat-icon>add_a_photo</mat-icon>
                </button>
                <span class="photo-count" *ngIf="damageFiles[borrow.productMovementHistoryID]?.length"
                      matTooltip="{{ 'asset.clearDamagePhotos' | translate }}"
                      (click)="clearDamagePhotos(borrow)">
                  {{ damageFiles[borrow.productMovementHistoryID].length }} <mat-icon class="detail-icon">close</mat-icon>
                </span>
                <button mat-raised-button color="primary"
                        [disabled]="isSaving"
                        (click)="onReturn(borrow)">
                  <mat-icon>keyboard_return</mat-icon>
                  {{ 'asset.return' | translate }}
                </button>
              </div>
            </div>
          </div>
          <div class="no-borrows" *ngIf="activeBorrows.length === 0">
            <mat-icon class="empty-icon">inventory</mat-icon>
            <p>{{ 'asset.noActiveBorrows' | translate }}</p>
          </div>
        </div>
      </mat-tab>

      <!-- New Borrow Tab -->
      <mat-tab [label]="'asset.newBorrow' | translate">
        <div class="tab-content short-content">
          <form [formGroup]="borrowForm" class="borrow-form">
            <!-- No storage/stock: asset can't be borrowed until it has a storage with stock.
                 Show a clear warning telling the user to assign a storage first. -->
            <div class="asset-no-storage-warning" *ngIf="!getSelectedStorage()">
              <mat-icon class="warn-icon">report_problem</mat-icon>
              <h3 class="warn-title">{{ 'asset.noStorageWarningTitle' | translate }}</h3>
              <p class="warn-body">{{ 'asset.noStorageWarningBody' | translate }}</p>
              <button mat-raised-button color="primary" type="button" (click)="onClose()">
                {{ 'close' | translate }}
              </button>
            </div>

            <ng-container *ngIf="getSelectedStorage()">
              <!-- Storage selection when the asset sits in multiple storages -->
              <mat-form-field appearance="outline" class="full-width" *ngIf="storages.length > 1">
                <mat-label>{{ 'storage' | translate }}</mat-label>
                <mat-select formControlName="storageStructureID" (selectionChange)="onStorageChange()">
                  <mat-option *ngFor="let s of storages" [value]="s.storageStructureID">
                    {{ s.storagePath }} ({{ s.net_quantity }})
                  </mat-option>
                </mat-select>
              </mat-form-field>

              <!-- Storage info (single storage) -->
              <div class="storage-info-display" *ngIf="storages.length <= 1">
                <mat-icon class="storage-icon">inventory_2</mat-icon>
                <div class="storage-details">
                  <span class="storage-label">{{ 'storage' | translate }}</span>
                  <span class="storage-path">{{ getSelectedStorage()?.storagePath }}</span>
                </div>
              </div>

              <!-- Quantity (hidden when only a single unit is available) -->
              <mat-form-field appearance="outline" class="full-width" *ngIf="getMaxQuantity() > 1">
                <mat-label>{{ 'quantity' | translate }}</mat-label>
                <input matInput type="number" formControlName="quantity" min="1" [max]="getMaxQuantity()">
                <mat-hint>{{ 'asset.availableInStorage' | translate: { count: getMaxQuantity() } }}</mat-hint>
                <mat-error *ngIf="borrowForm.get('quantity')?.hasError('required')">
                  {{ 'required' | translate }}
                </mat-error>
                <mat-error *ngIf="borrowForm.get('quantity')?.hasError('min') || borrowForm.get('quantity')?.hasError('max')">
                  {{ 'asset.invalidBorrowQuantity' | translate: { max: getMaxQuantity() } }}
                </mat-error>
              </mat-form-field>

              <!-- Borrower (User Selection) -->
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>{{ 'asset.borrowedBy' | translate }}</mat-label>
                <mat-select formControlName="borrowedByUsrID">
                  <mat-option *ngFor="let u of users" [value]="u.usrID">
                    {{ u.usrFirstName }} {{ u.usrLastName }}
                  </mat-option>
                </mat-select>
                <mat-error *ngIf="borrowForm.get('borrowedByUsrID')?.hasError('required')">
                  {{ 'asset.borrowerRequired' | translate }}
                </mat-error>
              </mat-form-field>

              <!-- Destination Note -->
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>{{ 'picking.destinationNote' | translate }}</mat-label>
                <input matInput formControlName="destinationNote"
                       [placeholder]="'picking.destinationNotePlaceholder' | translate">
              </mat-form-field>

              <!-- Expected Return Date (optional) -->
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>{{ 'picking.expectedReturnDate' | translate }}</mat-label>
                <input matInput [matDatepicker]="picker" [min]="minDate" formControlName="expectedReturnDate">
                <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
                <mat-datepicker #picker></mat-datepicker>
              </mat-form-field>

              <!-- Submit -->
              <div class="form-actions">
                <button mat-raised-button color="primary"
                        [disabled]="!borrowForm.valid || isSaving || !getSelectedStorage()"
                        (click)="onBorrow()">
                  <mat-icon>swap_horiz</mat-icon>
                  {{ 'picking.borrow' | translate }}
                </button>
              </div>
            </ng-container>
          </form>
        </div>
      </mat-tab>

      <!-- History Tab -->
      <mat-tab [label]="'asset.history' | translate">
        <div class="tab-content">
          <div class="history-list" *ngIf="borrowHistory.length > 0">
            <div class="history-item" *ngFor="let item of borrowHistory" [ngClass]="{'return-item': item.type === 'return'}">
              <div class="history-icon">
                <mat-icon *ngIf="item.type === 'borrow'">arrow_forward</mat-icon>
                <mat-icon *ngIf="item.type === 'return'">arrow_back</mat-icon>
              </div>
              <div class="history-info">
                <div class="history-header">
                  <span class="history-type" [ngClass]="item.type">
                    {{ item.type === 'borrow' ? ('asset.checkedOut' | translate) : ('asset.return' | translate) }}
                  </span>
                  <span class="history-date">{{ item.created_at | date:'MMM d, y h:mm a' }}</span>
                </div>
                <div class="history-details">
                  <span class="history-user">
                    <mat-icon class="detail-icon">person</mat-icon>
                    {{ item.userName }}
                  </span>
                  <span class="history-destination" *ngIf="item.destinationNote">
                    <mat-icon class="detail-icon">inventory_2</mat-icon>
                    {{ item.destinationNote }}
                  </span>
                  <span class="history-quantity" *ngIf="item.quantity > 1">
                    <mat-icon class="detail-icon">numbers</mat-icon>
                    {{ item.quantity }}
                  </span>
                  <a class="history-photos-link" *ngIf="item.damagePhotoCount > 0"
                     (click)="toggleDamagePhotos(item)">
                    <mat-icon class="detail-icon">photo_camera</mat-icon>
                    {{ item.damagePhotoCount }} {{ 'asset.damagePhotos' | translate }}
                  </a>
                </div>
                <div class="history-photo-thumbs" *ngIf="historyPhotos[item.productMovementHistoryID]">
                  <img *ngFor="let photo of historyPhotos[item.productMovementHistoryID]"
                       [src]="photo" (click)="openPhotoViewer(photo)">
                </div>
              </div>
            </div>
          </div>
          <div class="no-history" *ngIf="borrowHistory.length === 0">
            <mat-icon class="empty-icon">history</mat-icon>
            <p>{{ 'asset.noHistory' | translate }}</p>
          </div>
        </div>
      </mat-tab>

      <!-- Stock Adjustment Tab: set the total units of a multi-quantity asset -->
      <mat-tab [label]="'asset.stockAdjustment' | translate">
        <div class="tab-content">
          <form [formGroup]="adjustmentForm" class="borrow-form">
            <!-- Storage Selection -->
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>{{ 'storage' | translate }}</mat-label>
              <mat-select formControlName="storageStructureID" (selectionChange)="onAdjustmentStorageChange()">
                <mat-option *ngFor="let s of allStorages" [value]="s.storageStructureID">
                  {{ s.storagePath }} ({{ s.net_quantity }} {{ 'asset.currentStock' | translate }})
                </mat-option>
              </mat-select>
              <mat-error *ngIf="adjustmentForm.get('storageStructureID')?.hasError('required')">
                {{ 'asset.storageRequired' | translate }}
              </mat-error>
            </mat-form-field>

            <!-- New Stock Quantity -->
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>{{ 'asset.newStockQuantity' | translate }}</mat-label>
              <input matInput type="number" formControlName="newQuantity" min="0">
              <mat-hint>{{ 'asset.stockAdjustmentHint' | translate }}</mat-hint>
              <mat-error *ngIf="adjustmentForm.get('newQuantity')?.hasError('required')">
                {{ 'asset.quantityRequired' | translate }}
              </mat-error>
              <mat-error *ngIf="adjustmentForm.get('newQuantity')?.hasError('min')">
                {{ 'quantity' | translate }} must be at least 0
              </mat-error>
            </mat-form-field>

            <!-- Reason (Optional) -->
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>{{ 'reason' | translate }} ({{ 'optional' | translate }})</mat-label>
              <input matInput formControlName="reason" [placeholder]="'asset.adjustmentReasonPlaceholder' | translate">
            </mat-form-field>

            <!-- Submit -->
            <div class="form-actions">
              <button mat-raised-button color="primary"
                      [disabled]="!adjustmentForm.valid || isSaving"
                      (click)="onAdjust()">
                <mat-icon>tune</mat-icon>
                {{ 'asset.adjustStock' | translate }}
              </button>
            </div>

            <!-- No Storage Warning -->
            <div class="no-storage-warning" *ngIf="allStorages.length === 0">
              <mat-icon>warning</mat-icon>
              <span>{{ 'asset.noStoragesForAdjustment' | translate }}</span>
            </div>
          </form>
        </div>
      </mat-tab>
    </mat-tab-group>
  </div>

  <!-- Damage photo viewer (opened as a modal from history thumbnails) -->
  <ng-template #photoViewer>
    <div class="photo-viewer">
      <button mat-icon-button class="photo-viewer__close" mat-dialog-close>
        <mat-icon>close</mat-icon>
      </button>
      <img [src]="selectedPhoto">
    </div>
  </ng-template>
</div>
